import React, { useEffect, useRef, useCallback, useState } from 'react';
import _ from 'underscore';
import AceEditor from 'react-ace';
import 'ace-builds/src-min-noconflict/theme-monokai';
import 'ace-builds/src-min-noconflict/theme-tomorrow';
import 'ace-builds/src-min-noconflict/ext-language_tools';
import 'ace-builds/src-min-noconflict/ext-searchbox';
import 'ace-builds/src-min-noconflict/mode-c_cpp';
import 'ace-builds/src-min-noconflict/snippets/c_cpp.js';
import 'ace-builds/src-min-noconflict/mode-csharp';
import 'ace-builds/src-min-noconflict/snippets/csharp.js';
import 'ace-builds/src-min-noconflict/mode-golang';
import 'ace-builds/src-min-noconflict/snippets/golang.js';
import 'ace-builds/src-min-noconflict/mode-java';
import 'ace-builds/src-min-noconflict/snippets/java.js';
import 'ace-builds/src-min-noconflict/mode-javascript';
import 'ace-builds/src-min-noconflict/snippets/javascript.js';
import 'ace-builds/src-min-noconflict/mode-kotlin';
import 'ace-builds/src-min-noconflict/snippets/kotlin.js';
import 'ace-builds/src-min-noconflict/mode-php';
import 'ace-builds/src-min-noconflict/snippets/php.js';
import 'ace-builds/src-min-noconflict/mode-python';
import 'ace-builds/src-min-noconflict/snippets/python.js';
import 'ace-builds/src-min-noconflict/mode-ruby';
import 'ace-builds/src-min-noconflict/snippets/ruby.js';
import 'ace-builds/src-min-noconflict/mode-scala';
import 'ace-builds/src-min-noconflict/snippets/scala.js';
import 'ace-builds/src-min-noconflict/mode-typescript';
import 'ace-builds/src-min-noconflict/snippets/typescript.js';
import 'ace-builds/src-min-noconflict/mode-rust';
import 'ace-builds/src-min-noconflict/snippets/rust.js';
import 'ace-builds/src-min-noconflict/snippets/text.js';

import { categories } from 'constants/tests';
import { Languages } from 'constants/languages';
import {
  useFreecodeContextRun,
  useAppContext,
  useFreecodeContextLayout,
  useFreecodeContextData,
} from 'hooks/useContext';
import { EditorContainer, CodeContainer, SolutionButtonContainer } from './styles';
import { codeAutoCompleter } from './codeAutoCompleter';
import { layouts, editorTypes } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import { LanguageSelection } from 'components/FreecodePage/FreecodeTabs/FreecodeButtons';
import services from 'api/services';
import useFreecodeLivePrimus from 'components/FreecodePage/useFreecodeLivePrimus';
import './freecodeEditorSolution.css';
const { InputService, TicketService } = services;

export default function FreecodeEditorSolution() {
  const { editorType, layout } = useFreecodeContextLayout();
  const { ticket, test, testImpl, setTestImpl, languageData } = useFreecodeContextData();
  const { language, codeSol, setCodeSol, setCodeTestSolutions, levelToRun } = useFreecodeContextRun();
  const sendTicketPrimusRoomRequest = useFreecodeLivePrimus();

  const { isDarkMode } = useAppContext();

  const [markers, setMarkers] = useState([]);
  const [forceRender, setForceRender] = useState(false);

  const smallScreenRef = useRef(null);
  const editorRef = useRef(null);
  const copiedRef = useRef([]);
  const pastedRef = useRef({});
  const markerRef = useRef(null);
  const apiToFindRef = useRef(null);
  const scrollAttempts = useRef(0);

  const onChange = (value, event) => {
    if (editorType === editorTypes.creator || editorType === editorTypes.creatorInspect) {
      setTestImpl((oldTestImpl) => ({ ...oldTestImpl, solution: value }));
    } else if (test.category !== categories.CODE.category) {
      localStorage.setItem(`fsEditor-${test.category}-${test._id}-${language}`, value);
    } else if (editorType === editorTypes.freecode) {
      setCodeTestSolutions((oldSolutions) => ({ ...oldSolutions, [language]: value }));
      if (Object.keys(pastedRef.current).length) {
        const doSavePasteEvent = async (currentCode, currentPaste) => {
          if (!ticket?._id) return;
          const keysSaved = Object.keys(currentPaste);
          try {
            await InputService.autoSaveTicketCode({
              ticket: ticket._id,
              code: currentCode,
              language: language,
              pastedText: currentPaste,
            });
            for (const key of keysSaved) delete pastedRef.current[key];
          } catch (error) {
            console.error('Auto save error:', error);
          }
        };
        // when replacing with paste onChange first receive removal, only save on insert
        if (event?.action !== 'remove') doSavePasteEvent(value, pastedRef.current);
      }
      sendTicketDataToPrimus(value);
    }
    setCodeSol(value);
  };

  const sendTicketDataToPrimus = (value) => {
    let cursorPosition = editorRef.current.getCursorPosition();
    let fsEditorPosSol = {
      row: cursorPosition.row,
      column: cursorPosition.column,
      scrollRow: editorRef.current.getCursorPositionScreen().row,
    };
    if (ticket?._id)
      sendTicketPrimusRoomRequest({
        operation: 'ticketCodeData',
        solution: { code: value, language, fsEditorPosSol },
      });
  };

  const onPaste = (pastedValue) => {
    if (editorType !== editorTypes.freecode) return;
    if (copiedRef.current.includes(pastedValue)) return;
    if (!pastedValue || (codeSol || '').includes(pastedValue)) return;
    // Save pasted value here, but send to server in onChange to get the full updated code
    pastedRef.current[String(new Date())] = pastedValue;
  };

  const onCopy = (copiedText) => {
    if (editorType !== editorTypes.freecode) return;
    copiedRef.current.push(copiedText);
    try {
      TicketService.updateFreecodeCopyEvent(ticket._id, {
        date: new Date(),
        type: 'solution',
        data: copiedText,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const renderSmallScreenButtons = () => (
    <SolutionButtonContainer ref={smallScreenRef}>
      <LanguageSelection />
    </SolutionButtonContainer>
  );

  const getCompletions = useCallback(
    (_editor, session, pos, prefix, callback) => {
      codeAutoCompleter(session, pos, prefix, callback, testImpl?.generated?.autocompletions?.[language]);
    },
    [language, testImpl?.generated?.autocompletions]
  );

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.completers[0] = {
      getCompletions,
    };
  }, [getCompletions]);

  useEffect(() => {
    if (test.category !== categories.EXERCISE.category || editorType !== editorTypes.exercise) return;

    const session = editorRef.current.getSession();
    const apiIndex = _.findIndex(testImpl.api.solution, (api) => api.name.includes(`level ${levelToRun + 1}`));
    apiToFindRef.current = testImpl.generated?.apiStrings?.solution?.[language]?.[apiIndex];
    if (apiToFindRef.current) apiToFindRef.current = apiToFindRef.current.split('(')?.[0];

    const searchResult = editorRef.current.find(apiToFindRef.current);
    if (!searchResult || !apiToFindRef.current) {
      setMarkers([]);
      return session.setAnnotations([]);
    }
    const { start } = searchResult;
    markerRef.current = start.row;
    const rowContent = session.getLine(markerRef.current);

    setMarkers([
      {
        startRow: markerRef.current,
        startCol: 0,
        endRow: markerRef.current,
        endCol: rowContent.length,
        className: isDarkMode ? 'dark-marker' : 'light-marker',
      },
    ]);

    setForceRender((prev) => !prev);
  }, [test.category, isDarkMode, language, levelToRun, testImpl, editorType]);

  useEffect(() => {
    if (!apiToFindRef.current) return;
    editorRef.current.getSession().setAnnotations([
      {
        row: markerRef.current,
        text: 'Start from here',
        type: 'info',
      },
    ]);
    // Avoid running on load
    // Trigger the scroll-to method exclusively during level or language changes.
    // Used -6 because method should visible from top properly.
    if (scrollAttempts.current !== 1) editorRef.current.scrollToLine(markerRef.current - 6);
    scrollAttempts.current += 1;
  }, [forceRender]);

  const onEditorLoad = (editor) => {
    editorRef.current = editor;
    if (window.fsEditorPosSol) {
      editorRef.current.scrollToLine(window.fsEditorPosSol.scrollRow);
      editorRef.current.navigateTo(window.fsEditorPosSol.row, window.fsEditorPosSol.column);
      editorRef.current.focus();
      window.fsEditorPosSol = undefined;
    }
    if (editorRef.current.completers.length >= 4) return;
    let apiCompleter = {
      getCompletions,
    };
    editorRef.current.completers.unshift(apiCompleter);
  };

  const onBlur = () => {
    if (!editorRef.current) return;
    let cursorPosition = editorRef.current.getCursorPosition();
    editorRef.current.navigateTo(editorRef.current.getFirstVisibleRow(), 0);
    window.fsEditorPosSol = {
      row: cursorPosition.row,
      column: cursorPosition.column,
      scrollRow: editorRef.current.getCursorPositionScreen().row,
    };
  };

  return (
    <EditorContainer>
      {layout === layouts.small && renderSmallScreenButtons()}
      <CodeContainer smallScreenOffset={smallScreenRef.current?.clientHeight || 0}>
        <AceEditor
          placeholder="Please reset the code..."
          mode={Languages[language].EditorName}
          theme={isDarkMode ? 'monokai' : 'tomorrow'}
          fontSize={15}
          onLoad={onEditorLoad}
          onChange={onChange}
          onPaste={onPaste}
          onCopy={onCopy}
          onBlur={onBlur}
          value={codeSol}
          name="solutionEditorId"
          showPrintMargin={false}
          wrapEnabled={true}
          width={'100%'}
          height={'100%'}
          editorProps={{ $blockScrolling: true }}
          enableBasicAutocompletion={true}
          enableLiveAutocompletion={true}
          enableSnippets={true}
          tabSize={languageData[language]?.TabSize || 4}
          markers={markers}
          key={forceRender}
        />
      </CodeContainer>
    </EditorContainer>
  );
}
